/**
 * TemplateInsertModal.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState } from 'react';
import AssetSearchModal from './AssetSearchModal';
import './TemplateInsertModal.css';

const TemplateInsertModal = ({ template, onInsert, onCancel }) => {
  const [values, setValues] = useState(template.placeholders);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [currentAssetField, setCurrentAssetField] = useState(null);
  const [assetPickerType, setAssetPickerType] = useState('image');

  const handleAssetSelect = (asset) => {
    if (currentAssetField) {
      setValues(prev => ({
        ...prev,
        [currentAssetField]: asset.id.toString()
      }));
    }
    setShowAssetPicker(false);
    setCurrentAssetField(null);
  };

  const handleInsert = () => {
    let html = template.template;

    // Remove notation section if NOTATION_ASSET_ID is empty
    const notationAssetId = values.NOTATION_ASSET_ID?.trim();
    if (!notationAssetId) {
      // Remove the entire notation div section (from comment to closing div)
      // Matches: <!-- Musical Notation --> followed by the div and its contents
      html = html.replace(
        /<!-- Musical Notation -->\s*<div class="tune-notation">[\s\S]*?<\/div>\s*/g,
        ''
      );
    }

    // Remove audio section if AUDIO_ASSET_ID is empty
    const audioAssetId = values.AUDIO_ASSET_ID?.trim();
    if (!audioAssetId) {
      // Remove the entire audio div section (from comment to closing div)
      // The audio section has nested divs, so we need to count to find the matching closing tag
      let startIndex = html.indexOf('<!-- Audio Section -->');
      if (startIndex !== -1) {
        // Find the opening div tag
        const divStart = html.indexOf('<div class="tune-audio"', startIndex);
        if (divStart !== -1) {
          // Find the matching closing div by counting opening/closing tags
          let depth = 0;
          let i = divStart;
          let found = false;
          while (i < html.length && !found) {
            if (html.substring(i, i + 4) === '<div') {
              depth++;
            } else if (html.substring(i, i + 6) === '</div>') {
              depth--;
              if (depth === 0) {
                // Found the matching closing tag - remove from comment to end of closing div
                const endIndex = i + 6;
                // Also remove any trailing whitespace/newlines
                let actualEnd = endIndex;
                while (actualEnd < html.length && (html[actualEnd] === ' ' || html[actualEnd] === '\n' || html[actualEnd] === '\r' || html[actualEnd] === '\t')) {
                  actualEnd++;
                }
                html = html.substring(0, startIndex) + html.substring(actualEnd);
                found = true;
              }
            }
            i++;
          }
        }
      }
    }

    // Replace all placeholders with actual values
    Object.entries(values).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      html = html.replace(regex, value || '');
    });

    onInsert(html);
  };

  const handleAssetPickerOpen = (fieldKey) => {
    setCurrentAssetField(fieldKey);
    // Determine asset type based on field name
    if (fieldKey.includes('AUDIO') || fieldKey.includes('MUSIC')) {
      setAssetPickerType('music');
    } else if (fieldKey.includes('IMAGE') || fieldKey.includes('NOTATION')) {
      setAssetPickerType('image');
    } else {
      setAssetPickerType('image'); // default
    }
    setShowAssetPicker(true);
  };

  const formatFieldLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  return (
    <>
      <div className="modal-overlay" onClick={onCancel}>
        <div className="template-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>📄 {template.name}</h3>
            <button className="close-btn" onClick={onCancel}>×</button>
          </div>

          <div className="modal-body">
            <p className="template-description">{template.description}</p>

            <div className="template-fields">
              {Object.entries(template.placeholders).map(([key, defaultValue]) => {
                const hint = template.hints?.[key] || defaultValue || formatFieldLabel(key);
                return (
                  <div key={key} className="template-field">
                    <label>{formatFieldLabel(key)}</label>
                    {key.includes('ASSET_ID') ? (
                      <div className="asset-field-group">
                        <input
                          type="text"
                          value={values[key]}
                          onChange={(e) => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={hint}
                        />
                        <button
                          type="button"
                          className="asset-picker-btn"
                          onClick={() => handleAssetPickerOpen(key)}
                        >
                          🔍 Browse
                        </button>
                      </div>
                    ) : key.includes('DESCRIPTION') ? (
                      <textarea
                        value={values[key]}
                        onChange={(e) => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                        rows="4"
                        placeholder={hint}
                      />
                    ) : (
                      <input
                        type="text"
                        value={values[key]}
                        onChange={(e) => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={hint}
                      />
                    )}
                    <small className="field-hint">
                      {key.includes('ASSET_ID') && 'Click Browse to search for an asset in the database'}
                      {key === 'ORIGINAL_URL' && 'Optional: Link to the original page if migrating content'}
                    </small>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleInsert}>
              ✅ Insert Template
            </button>
          </div>
        </div>
      </div>

      {showAssetPicker && (
        <AssetSearchModal
          isOpen={showAssetPicker}
          onSelectAsset={handleAssetSelect}
          onClose={() => {
            setShowAssetPicker(false);
            setCurrentAssetField(null);
          }}
          fileType={assetPickerType}
        />
      )}
    </>
  );
};

export default TemplateInsertModal;

